import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toastSuccess, warning } from '../../../../../components/Alert/Alert';
import Button from '../../../../../components/Button/Button';
import Input from '../../../../../components/Input/Input';
import { SelectCreate } from '../../../../../components/SelectCreate/SelectCreate';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import FormikMaterial from '../../../../MACRA_1/components/InputForm/Materials/ModalMaterial/FormikMaterial';
import { IMaterialResponse } from '../../../../api/IApiResponse';
import { EConversionSIToImperial, conversionLabel, convertTo } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setStillingBasin } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { EElevation, ISEElevation } from '../../graphicalResults/crossSection/enum/common';
import { IStillingBasinProps, IStillingBasinSteppedWeirSlopedWeirForm } from './IStillingBasin';
import { UnitOfMeasure } from '../../../../enum/project';

const elevationOptions = [
    { value: EElevation.zeroPointThree, label: '0.3' },
    { value: EElevation.zeroPointFive, label: '0.5' },
    { value: EElevation.one, label: '1' },
    { value: EElevation.onePointFive, label: '1.5' },
];
const elevationOptionsImp = [
    { value: ISEElevation.one, label: '1' },
    { value: ISEElevation.oneAndHalf, label: '1.5' },
    { value: ISEElevation.three, label: '3' },
    { value: ISEElevation.fourAndHalf, label: '4.5' },
];
const StillingBasinSteppedWeirSlopedWeir = (props: IStillingBasinProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);

    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const defaultValue: IStillingBasinSteppedWeirSlopedWeirForm = {
        isCounterweir: project.data!.stillingBasin!.isCounterweir,
        counterweirElevation: project.data!.stillingBasin!.counterweirElevation,
        poolLength: project.data!.stillingBasin!.poolLength,
        isStillingBasin: project.data!.stillingBasin!.isStillingBasin,
        width: project.data!.stillingBasin!.width,
        stillingBasinElevation: project.data!.stillingBasin!.stillingBasinElevation,
        thickness: project.data!.stillingBasin!.thickness,
        material: project.data!.stillingBasin!.material,
    };

    const [form, setForm] = useState<IStillingBasinSteppedWeirSlopedWeirForm>(defaultValue);
    const [openMaterials, setOpenMaterials] = useState<boolean>(false);

    const warningTimer = useRef<NodeJS.Timeout | null>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = e.target.type === 'checkbox' ? e.target.checked : parseFloat(e.target.value);
        let oneToImp = isImperial ? convertTo(1, EConversionSIToImperial.m_to_ft) : 1;

        if (warningTimer.current) {
            clearTimeout(warningTimer.current);
            }
        //input validation
        if (name == 'poolLength' && (value as number) < oneToImp) {
            warningTimer.current = setTimeout(() => {
            value = 1;
            warning(t('warning_not_be_lower', { value: oneToImp }));
            setForm({
                ...form,
                poolLength: oneToImp,
            });
            }, 3000);
        } else if ((value as number) < 0) {
            warningTimer.current = setTimeout(() => {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
            }, 3000);
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setStillingBasin(form as IStillingBasinSteppedWeirSlopedWeirForm));
        }, 1000);
    }, [form]);

    const openModalMaterials = (e: React.MouseEvent<HTMLButtonElement>) => {
        setOpenMaterials(true);
    };
    const updateMaterial = (data: IMaterialResponse) => {
        setForm({ ...form, material: data });
    };
    return (
        <div className='d-flex flex-column gap-2'>
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='counterweirElevation'>{t('input_counterweirElevation', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</label>
                <SelectCreate
                    value={form.counterweirElevation}
                    name='counterweirElevation'
                    options={isImperial ? elevationOptionsImp : elevationOptions}
                    parentOnChange={(newValue: any) =>
                        setForm({
                            ...form!,
                            counterweirElevation: newValue,
                        })
                    }
                />
            </div>
            <Input
                type='number'
                title={t('input_length', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                name='poolLength'
                value={form.poolLength}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_basinWidth', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                name='width'
                value={form.width}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />

            <Button classNameBtn='btn-materials' onClick={openModalMaterials}>
                {t('input_selectMaterial')}
            </Button>
            {openMaterials && (
                <FormikMaterial
                    material={form.material}
                    isMacra2
                    open={openMaterials}
                    materialId={form.material?.id}
                    closeModal={() => setOpenMaterials(false)}
                    onClose={data => {
                        updateMaterial(data!), toastSuccess(t('modal_material_saved'));
                    }}
                />
            )}
        </div>
    );
};

export default StillingBasinSteppedWeirSlopedWeir;
